import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  X, 
  Clock, 
  AlertCircle,
  ChevronLeft,
  Flag,
  Eye
} from 'react-feather';
import confetti from 'canvas-confetti';
import styles from '../styles/immersiveTest.module.css';

const ImmersiveTest = ({ test, config, onComplete, onExit }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState(new Set());
  const [showFeedback, setShowFeedback] = useState(false);
  const [answersLocked, setAnswersLocked] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(config.timeLimit * 60); // in seconds
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const intervalRef = useRef(null);

  const questions = test?.questions || [];
  const currentQ = questions[currentQuestion] || {};

  // Timer
  useEffect(() => {
    if (config.timeLimit > 0 && !showResults && !showReview) {
      intervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleSubmitTest();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.timeLimit, showResults, showReview]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswer = (answer) => {
    if (answersLocked) return;
    setAnswers({ ...answers, [currentQuestion]: answer });
    setShowFeedback(false);
  };

  const handleCheckAnswer = () => {
    if (!answers[currentQuestion] && currentQ.type !== 'written') return;
    setAnswersLocked(true);
    setShowFeedback(true);
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setShowFeedback(false);
      setAnswersLocked(false);
    } else {
      handleSubmitTest();
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setShowFeedback(false);
      setAnswersLocked(false);
    }
  };

  const toggleFlag = () => {
    const newFlagged = new Set(flagged);
    if (newFlagged.has(currentQuestion)) {
      newFlagged.delete(currentQuestion);
    } else {
      newFlagged.add(currentQuestion);
    }
    setFlagged(newFlagged);
  };

  const handleSubmitTest = () => {
    clearInterval(intervalRef.current);
    
    // Calculate results
    let correct = 0;
    let incorrect = 0;
    let unanswered = 0;
    let needsReview = 0;

    const questionResults = questions.map((q, idx) => {
      const userAnswer = answers[idx];
      let isCorrect = false;

      if (!userAnswer) {
        unanswered++;
      } else {
        // Check answer based on question type
        if (q.type === 'multipleChoice' || q.type === 'trueFalse') {
          const correctAnswers = Array.isArray(q.correctAnswers) 
            ? q.correctAnswers 
            : [q.correctAnswer];
          isCorrect = correctAnswers.includes(userAnswer);
        } else if (q.type === 'fillBlank') {
          const correctAnswers = Array.isArray(q.correctAnswers)
            ? q.correctAnswers.map(a => a.toLowerCase().trim())
            : [q.correctAnswer.toLowerCase().trim()];
          isCorrect = correctAnswers.includes(userAnswer.toLowerCase().trim());
        } else if (q.type === 'written') {
          // For written, we can't auto-grade, so mark as needs review
          isCorrect = null;
          needsReview++;
        }

        if (isCorrect === true) correct++;
        else if (isCorrect === false) incorrect++;
      }

      return {
        question: q,
        userAnswer,
        isCorrect
      };
    });

    // Calculate score only from gradeable questions (exclude written)
    const gradeableQuestions = questions.filter(q => q.type !== 'written').length;
    const score = gradeableQuestions > 0 ? Math.round((correct / gradeableQuestions) * 100) : 0;

    setResults({
      score,
      correct,
      incorrect,
      unanswered,
      needsReview,
      total: questions.length,
      timeUsed: config.timeLimit > 0 ? config.timeLimit * 60 - timeRemaining : null,
      questionResults
    });

    setShowResults(true);

    // Confetti if passing score
    if (score >= 70) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }

    if (onComplete) {
      onComplete(results);
    }
  };

  const isAnswered = (idx) => answers[idx] !== undefined && answers[idx] !== '';
  const isCurrentAnswered = isAnswered(currentQuestion);

  const checkAnswer = () => {
    if (currentQ.type === 'multipleChoice' || currentQ.type === 'trueFalse') {
      const correctAnswers = Array.isArray(currentQ.correctAnswers) 
        ? currentQ.correctAnswers 
        : [currentQ.correctAnswer];
      return correctAnswers.includes(answers[currentQuestion]);
    } else if (currentQ.type === 'fillBlank') {
      const correctAnswers = Array.isArray(currentQ.correctAnswers)
        ? currentQ.correctAnswers.map(a => a.toLowerCase().trim())
        : [currentQ.correctAnswer.toLowerCase().trim()];
      return correctAnswers.includes((answers[currentQuestion] || '').toLowerCase().trim());
    }
    return false;
  };

  // Results Screen
  if (showResults) {
    return (
      <div className={styles.testWrapper}>
        <div className={styles.resultsContainer}>
          <motion.div 
            className={styles.resultsCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className={styles.resultsHeader}>
              <h2 className={styles.resultsTitle}>Test Complete!</h2>
              <p className={styles.resultsSubtitle}>{test.title || config.topic}</p>
            </div>

            <div className={styles.scoreCircle}>
              <svg viewBox="0 0 200 200" className={styles.scoreSvg}>
                <circle
                  cx="100"
                  cy="100"
                  r="85"
                  fill="none"
                  stroke="rgba(255,255,255,0.05)"
                  strokeWidth="12"
                />
                <motion.circle
                  cx="100"
                  cy="100"
                  r="85"
                  fill="none"
                  stroke={results.score >= 70 ? '#10b981' : results.score >= 50 ? '#3b82f6' : '#ef4444'}
                  strokeWidth="12"
                  strokeLinecap="round"
                  strokeDasharray={534.07}
                  strokeDashoffset={534.07 - (534.07 * results.score) / 100}
                  transform="rotate(-90 100 100)"
                  initial={{ strokeDashoffset: 534.07 }}
                  animate={{ strokeDashoffset: 534.07 - (534.07 * results.score) / 100 }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                />
              </svg>
              <div className={styles.scoreContent}>
                <div className={styles.scoreNumber}>{results.score}%</div>
                <div className={styles.scoreLabel}>Score</div>
              </div>
            </div>

            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <div className={styles.statIcon} style={{ background: '#10b981' }}>
                  <Check size={20} />
                </div>
                <div className={styles.statValue}>{results.correct}</div>
                <div className={styles.statLabel}>Correct</div>
              </div>

              <div className={styles.statCard}>
                <div className={styles.statIcon} style={{ background: '#ef4444' }}>
                  <X size={20} />
                </div>
                <div className={styles.statValue}>{results.incorrect}</div>
                <div className={styles.statLabel}>Incorrect</div>
              </div>

              <div className={styles.statCard}>
                <div className={styles.statIcon} style={{ background: '#f59e0b' }}>
                  <AlertCircle size={20} />
                </div>
                <div className={styles.statValue}>{results.unanswered}</div>
                <div className={styles.statLabel}>Skipped</div>
              </div>

              {results.needsReview > 0 && (
                <div className={styles.statCard}>
                  <div className={styles.statIcon} style={{ background: '#8b5cf6' }}>
                    <Eye size={20} />
                  </div>
                  <div className={styles.statValue}>{results.needsReview}</div>
                  <div className={styles.statLabel}>Needs Review</div>
                </div>
              )}

              {results.timeUsed && (
                <div className={styles.statCard}>
                  <div className={styles.statIcon} style={{ background: '#3b82f6' }}>
                    <Clock size={20} />
                  </div>
                  <div className={styles.statValue}>{formatTime(results.timeUsed)}</div>
                  <div className={styles.statLabel}>Time</div>
                </div>
              )}
            </div>

            <div className={styles.resultsActions}>
              <motion.button
                className={styles.primaryBtn}
                onClick={() => setShowReview(true)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Eye size={18} />
                Review Answers
              </motion.button>

              {onExit && (
                <motion.button
                  className={styles.secondaryBtn}
                  onClick={onExit}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Exit Test
                </motion.button>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Review Screen
  if (showReview) {
    return (
      <div className={styles.testWrapper}>
        <div className={styles.reviewContainer}>
          <div className={styles.reviewHeader}>
            <button className={styles.backBtn} onClick={() => setShowReview(false)}>
              <ChevronLeft size={20} />
              Back to Results
            </button>
            <h2 className={styles.reviewTitle}>Answer Review</h2>
          </div>

          <div className={styles.reviewList}>
            {results.questionResults.map((result, idx) => (
              <motion.div
                key={idx}
                className={`${styles.reviewCard} ${
                  result.isCorrect === true ? styles.reviewCardCorrect :
                  result.isCorrect === false ? styles.reviewCardIncorrect :
                  styles.reviewCardUnanswered
                }`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <div className={styles.reviewCardHeader}>
                  <div className={styles.reviewQuestionNum}>Question {idx + 1}</div>
                  <div className={styles.reviewStatus}>
                    {result.isCorrect === true && <Check size={18} />}
                    {result.isCorrect === false && <X size={18} />}
                    {result.isCorrect === null && <AlertCircle size={18} />}
                  </div>
                </div>

                <div className={styles.reviewQuestion}>{result.question.question || result.question.prompt}</div>

                {result.userAnswer && (
                  <div className={styles.reviewAnswer}>
                    <span className={styles.reviewAnswerLabel}>Your Answer:</span>
                    <span className={styles.reviewAnswerValue}>{result.userAnswer}</span>
                  </div>
                )}

                {result.isCorrect === null && result.question.type === 'written' && (
                  <div className={styles.reviewExplanation} style={{background: 'rgba(251, 191, 36, 0.1)', borderColor: '#f59e0b'}}>
                    <strong>⚠️ Needs Manual Review:</strong> This written response requires manual evaluation. {result.question.explanation || result.question.correctAnswer}
                  </div>
                )}

                {result.isCorrect === false && result.question.correctAnswer && result.question.type !== 'written' && (
                  <div className={styles.reviewCorrect}>
                    <span className={styles.reviewCorrectLabel}>Correct Answer:</span>
                    <span className={styles.reviewCorrectValue}>{result.question.correctAnswer}</span>
                  </div>
                )}

                {result.question.explanation && config.showFeedback && result.question.type !== 'written' && (
                  <div className={styles.reviewExplanation}>
                    <strong>Explanation:</strong> {result.question.explanation}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Test Taking Screen
  return (
    <div className={styles.testWrapper}>
      <div className={styles.testContainer}>
        {/* Header */}
        <div className={styles.testHeader}>
          <div className={styles.testInfo}>
            <h1 className={styles.testTitle}>{test.title || config.topic}</h1>
            <p className={styles.testMeta}>
              Question {currentQuestion + 1} of {questions.length}
            </p>
          </div>

          <div className={styles.testControls}>
            {config.timeLimit > 0 && (
              <div className={`${styles.timer} ${timeRemaining < 60 ? styles.timerWarning : ''}`}>
                <Clock size={18} />
                <span>{formatTime(timeRemaining)}</span>
              </div>
            )}

            <button className={styles.flagBtn} onClick={toggleFlag}>
              <Flag size={18} fill={flagged.has(currentQuestion) ? 'currentColor' : 'none'} />
            </button>

            {onExit && (
              <button className={styles.exitBtn} onClick={onExit}>
                Exit
              </button>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className={styles.progressBar}>
          <motion.div
            className={styles.progressFill}
            initial={{ width: 0 }}
            animate={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>

        {/* Question Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            className={styles.questionContainer}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.questionHeader}>
              <div className={styles.questionType}>
                {currentQ.type === 'multipleChoice' && 'Multiple Choice'}
                {currentQ.type === 'fillBlank' && 'Fill in the Blank'}
                {currentQ.type === 'written' && 'Written Response'}
                {currentQ.type === 'trueFalse' && 'True or False'}
              </div>
              <div className={styles.questionPoints}>
                {currentQ.points || 1} {currentQ.points === 1 ? 'point' : 'points'}
              </div>
            </div>

            <h2 className={styles.questionText}>
              {currentQ.question || currentQ.prompt}
            </h2>

            {/* Answer Input */}
            <div className={styles.answerSection}>
              {(currentQ.type === 'multipleChoice' || currentQ.type === 'trueFalse') && (
                <div className={styles.choicesGrid}>
                  {(currentQ.choices || currentQ.options || []).map((choice, idx) => {
                    const choiceId = typeof choice === 'object' ? choice.id : String.fromCharCode(65 + idx);
                    const choiceText = typeof choice === 'object' ? choice.text : choice;
                    const isSelected = answers[currentQuestion] === choiceId || answers[currentQuestion] === choiceText;
                    
                    let feedbackClass = '';
                    if (showFeedback && config.showFeedback) {
                      const correctAnswers = Array.isArray(currentQ.correctAnswers) 
                        ? currentQ.correctAnswers 
                        : [currentQ.correctAnswer];
                      const isCorrectChoice = correctAnswers.includes(choiceId) || correctAnswers.includes(choiceText);
                      
                      if (isSelected && isCorrectChoice) feedbackClass = styles.choiceCorrect;
                      else if (isSelected && !isCorrectChoice) feedbackClass = styles.choiceIncorrect;
                      else if (isCorrectChoice) feedbackClass = styles.choiceCorrect;
                    }

                    return (
                      <button
                        key={idx}
                        className={`${styles.choiceBtn} ${isSelected ? styles.choiceSelected : ''} ${feedbackClass}`}
                        onClick={() => handleAnswer(choiceId || choiceText)}
                        disabled={answersLocked}
                      >
                        <span className={styles.choiceLabel}>{choiceId || String.fromCharCode(65 + idx)}</span>
                        <span className={styles.choiceText}>{choiceText}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {currentQ.type === 'fillBlank' && (
                <input
                  type="text"
                  className={styles.fillBlankInput}
                  placeholder="Type your answer here..."
                  value={answers[currentQuestion] || ''}
                  onChange={(e) => handleAnswer(e.target.value)}
                  disabled={answersLocked}
                />
              )}

              {currentQ.type === 'written' && (
                <textarea
                  className={styles.writtenInput}
                  placeholder="Type your response here..."
                  value={answers[currentQuestion] || ''}
                  onChange={(e) => handleAnswer(e.target.value)}
                  disabled={answersLocked}
                  rows={8}
                />
              )}
            </div>

            {/* Feedback */}
            {showFeedback && config.showFeedback && currentQ.explanation && (
              <motion.div
                className={`${styles.feedback} ${checkAnswer() ? styles.feedbackCorrect : styles.feedbackIncorrect}`}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className={styles.feedbackHeader}>
                  {checkAnswer() ? (
                    <>
                      <Check size={20} />
                      <span>Correct!</span>
                    </>
                  ) : (
                    <>
                      <X size={20} />
                      <span>Incorrect</span>
                    </>
                  )}
                </div>
                <p className={styles.feedbackText}>{currentQ.explanation}</p>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className={styles.navigation}>
          <button
            className={styles.navBtn}
            onClick={handlePrev}
            disabled={currentQuestion === 0}
          >
            <ArrowLeft size={18} />
            Previous
          </button>

          <div className={styles.centerActions}>
            {!showFeedback && isCurrentAnswered && config.showFeedback && (
              <button
                className={styles.checkBtn}
                onClick={handleCheckAnswer}
              >
                Check Answer
              </button>
            )}

            <div className={styles.questionIndicators}>
              {questions.slice(0, 10).map((_, idx) => (
                <button
                  key={idx}
                  className={`${styles.indicator} ${
                    idx === currentQuestion ? styles.indicatorActive :
                    isAnswered(idx) ? styles.indicatorAnswered :
                    flagged.has(idx) ? styles.indicatorFlagged : ''
                  }`}
                  onClick={() => {
                    setCurrentQuestion(idx);
                    setShowFeedback(false);
                    setAnswersLocked(false);
                  }}
                  title={`Question ${idx + 1}`}
                />
              ))}
              {questions.length > 10 && (
                <span className={styles.indicatorMore}>+{questions.length - 10}</span>
              )}
            </div>
          </div>

          <button
            className={styles.navBtnPrimary}
            onClick={handleNext}
          >
            {currentQuestion === questions.length - 1 ? 'Submit Test' : 'Next'}
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImmersiveTest;
